import { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useParticipationAnalytics, useProductBreakdown } from '../api';
import { useScope } from '../../store/scope';
import { Stat, PageLoader, ErrorState, EmptyState } from '../../components/ui';
import { money, num, pct, dateShort } from '../../lib/format';

type Granularity = 'day' | 'week' | 'month';
const GOLD = '#bd9142';
const INK = '#161619';

export function AnalyticsPage() {
  const { participationId } = useScope();
  const [granularity, setGranularity] = useState<Granularity>('day');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [prodTab, setProdTab] = useState<'highestRevenue' | 'mostBooked' | 'mostViewed'>('highestRevenue');

  const params = { granularity, from: from || undefined, to: to || undefined };
  const dash = useParticipationAnalytics(participationId, params);
  const products = useProductBreakdown(participationId, params);

  const charts = dash.data?.charts;
  const merged = useMemo(() => mergeSeries(charts), [charts]);

  if (!participationId) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-ink-900">Analytics</h1>
        <EmptyState
          title="Choose an expo first"
          hint="Pick an exhibition from the “Expo focus” selector in the top bar to see its funnel and breakdowns."
        />
      </div>
    );
  }

  if (dash.isLoading) return <PageLoader />;
  if (dash.error) return <ErrorState message={(dash.error as Error).message} onRetry={() => dash.refetch()} />;

  const s = dash.data!.summary;
  const stalls = dash.data!.stalls ?? [];
  const racks = dash.data!.racks ?? [];
  const prodRows = (products.data?.[prodTab] ?? []) as ProdRow[];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink-900">Analytics</h1>
          <p className="text-sm text-ink-400">{dash.data?.participation?.expoName}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="block">
            <span className="label">From</span>
            <input type="date" className="input h-9 py-1" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="block">
            <span className="label">To</span>
            <input type="date" className="input h-9 py-1" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <label className="block">
            <span className="label">Granularity</span>
            <select
              className="input h-9 w-auto py-1"
              value={granularity}
              onChange={(e) => setGranularity(e.target.value as Granularity)}
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </select>
          </label>
        </div>
      </div>

      {/* Funnel KPIs */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Stat label="QR scans" value={num(s.scans)} sub={`${num(s.uniqueVisitors)} unique`} />
        <Stat label="Product views" value={num(s.productViews)} sub={`${num(s.uniqueViewers)} unique`} />
        <Stat label="Bookings" value={num(s.bookings)} sub={`${num(s.quantityBooked)} pieces`} />
        <Stat label="View rate" value={pct(s.viewRatePct)} sub="scan → view" />
        <Stat label="Conversion" value={pct(s.conversionRatePct)} sub="visitor → booking" tone="emerald" />
        <Stat label="Booking value" value={money(s.revenue.bookingValue)} tone="gold" />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Confirmed revenue" value={money(s.revenue.confirmedRevenue)} tone="emerald" />
        <Stat label="Completed revenue" value={money(s.revenue.completedRevenue)} tone="emerald" />
        <Stat label="Cancelled value" value={money(s.revenue.cancelledValue)} tone="red" />
        <Stat label="Avg booking" value={money(s.revenue.averageBookingValue)} />
      </div>

      {/* Charts */}
      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="Revenue by date">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={charts?.revenueByDate ?? []}>
              <defs>
                <linearGradient id="arev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={GOLD} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tickFormatter={(d) => dateShort(d)} fontSize={11} />
              <YAxis fontSize={11} width={64} tickFormatter={(v) => num(v)} />
              <Tooltip formatter={(v) => money(v as number)} labelFormatter={(d) => dateShort(d as string)} />
              <Area type="monotone" dataKey="revenue" stroke={GOLD} fill="url(#arev)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Scans & bookings by date">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={merged}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tickFormatter={(d) => dateShort(d)} fontSize={11} />
              <YAxis fontSize={11} width={40} />
              <Tooltip labelFormatter={(d) => dateShort(d as string)} />
              <Bar dataKey="scans" fill="#d0d0d6" radius={[3, 3, 0, 0]} />
              <Bar dataKey="bookings" fill={INK} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Revenue by stall">
          <ResponsiveContainer width="100%" height={Math.max(160, (charts?.revenueByStall?.length ?? 1) * 34)}>
            <BarChart data={charts?.revenueByStall ?? []} layout="vertical" margin={{ left: 12 }}>
              <XAxis type="number" fontSize={11} tickFormatter={(v) => num(v)} />
              <YAxis type="category" dataKey="stall" fontSize={11} width={70} />
              <Tooltip formatter={(v) => money(v as number)} />
              <Bar dataKey="revenue" fill={GOLD} radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Revenue by rack">
          <ResponsiveContainer width="100%" height={Math.max(160, (charts?.revenueByRack?.length ?? 1) * 30)}>
            <BarChart data={charts?.revenueByRack ?? []} layout="vertical" margin={{ left: 12 }}>
              <XAxis type="number" fontSize={11} tickFormatter={(v) => num(v)} />
              <YAxis type="category" dataKey="rack" fontSize={11} width={70} />
              <Tooltip formatter={(v) => money(v as number)} />
              <Bar dataKey="revenue" fill={INK} radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Category performance */}
      {(charts?.categoryPerformance?.length ?? 0) > 0 && (
        <ChartCard title="Category performance">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="table-head">
                  <th className="px-3 py-2">Category</th>
                  <th className="px-3 py-2 text-right">Bookings</th>
                  <th className="px-3 py-2 text-right">Quantity</th>
                  <th className="px-3 py-2 text-right">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-50">
                {charts!.categoryPerformance.map((c: CatRow) => (
                  <tr key={c.category}>
                    <td className="px-3 py-2">{c.category}</td>
                    <td className="px-3 py-2 text-right">{num(c.bookings)}</td>
                    <td className="px-3 py-2 text-right">{num(c.quantity)}</td>
                    <td className="px-3 py-2 text-right font-medium text-gold-600">{money(c.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>
      )}

      {/* Stall breakdown */}
      <BreakdownTable
        title="Stall breakdown"
        headers={['Stall', 'Scans', 'Visitors', 'Bookings', 'Qty', 'Booking value', 'Conv.']}
        rows={stalls.map((r: any) => [
          r.stallName ? `${r.stallNumber} — ${r.stallName}` : r.stallNumber,
          num(r.scans),
          num(r.uniqueVisitors),
          num(r.bookings),
          num(r.quantity),
          money(r.bookingValue),
          pct(r.conversionRatePct),
        ])}
      />

      {/* Rack breakdown */}
      <BreakdownTable
        title="Rack breakdown"
        headers={['Rack', 'Scans', 'Visitors', 'Views', 'Bookings', 'Qty', 'Booking value']}
        rows={racks.map((r: any) => [
          r.rackNumber,
          num(r.scans),
          num(r.uniqueVisitors),
          num(r.productViews),
          num(r.bookings),
          num(r.quantity),
          money(r.bookingValue),
        ])}
      />

      {/* Product leaderboard */}
      <ChartCard title="Products">
        <div className="mb-3 flex gap-1 print:hidden">
          {(
            [
              ['highestRevenue', 'Top revenue'],
              ['mostBooked', 'Most booked'],
              ['mostViewed', 'Most viewed'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              className={`rounded-md px-3 py-1 text-sm ${prodTab === k ? 'bg-ink-900 text-white' : 'hover:bg-ink-100'}`}
              onClick={() => setProdTab(k)}
            >
              {label}
            </button>
          ))}
        </div>
        {products.isLoading ? (
          <PageLoader label="…" />
        ) : prodRows.length === 0 ? (
          <p className="p-2 text-sm text-ink-400">No product activity in this range yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="table-head">
                  <th className="px-3 py-2">Product</th>
                  <th className="px-3 py-2 text-right">Views</th>
                  <th className="px-3 py-2 text-right">Bookings</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Revenue</th>
                  <th className="px-3 py-2 text-right">Conv.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-50">
                {prodRows.map((p) => (
                  <tr key={p.productId}>
                    <td className="px-3 py-2">
                      <span className="font-medium">{p.name}</span>
                      <span className="ml-2 font-mono text-xs text-ink-400">#{p.barcodeNo}</span>
                    </td>
                    <td className="px-3 py-2 text-right">{num(p.productViews)}</td>
                    <td className="px-3 py-2 text-right">{num(p.bookings)}</td>
                    <td className="px-3 py-2 text-right">{num(p.quantity)}</td>
                    <td className="px-3 py-2 text-right font-medium text-gold-600">{money(p.revenue)}</td>
                    <td className="px-3 py-2 text-right">{pct(p.conversionRatePct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ChartCard>
    </div>
  );
}

interface ProdRow {
  productId: string;
  barcodeNo: string;
  name: string;
  productViews: number;
  bookings: number;
  quantity: number;
  revenue: number;
  conversionRatePct: number;
}
interface CatRow {
  category: string;
  bookings: number;
  quantity: number;
  revenue: number;
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card p-4">
      <h2 className="mb-2 text-sm font-semibold text-ink-700">{title}</h2>
      {children}
    </div>
  );
}

function BreakdownTable({
  title,
  headers,
  rows,
}: {
  title: string;
  headers: string[];
  rows: (string | number)[][];
}) {
  return (
    <div className="card overflow-hidden">
      <h2 className="border-b border-ink-100 px-4 py-3 text-sm font-semibold text-ink-700">{title}</h2>
      {rows.length === 0 ? (
        <p className="p-4 text-sm text-ink-400">No data.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="table-head">
                {headers.map((h, i) => (
                  <th key={h} className={`px-3 py-2 ${i === 0 ? '' : 'text-right'}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {rows.map((r, ri) => (
                <tr key={ri} className="hover:bg-ink-50/60">
                  {r.map((cell, ci) => (
                    <td key={ci} className={`px-3 py-2 ${ci === 0 ? 'font-medium' : 'text-right'}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function mergeSeries(charts: any) {
  if (!charts) return [];
  const map = new Map<string, any>();
  for (const s of charts.scansByDate ?? []) map.set(s.date, { date: s.date, scans: s.scans, bookings: 0 });
  for (const b of charts.bookingsByDate ?? []) {
    const row = map.get(b.date) ?? { date: b.date, scans: 0, bookings: 0 };
    row.bookings = b.bookings;
    map.set(b.date, row);
  }
  return [...map.values()].sort((a, b) => String(a.date).localeCompare(String(b.date)));
}
